import { reactive } from 'vue'
import type { Position, NavItem } from '../types/NavigationPane'
import { defaultConfig } from '../constants/navigationPaneDefaults'
import { merge } from 'lodash'

interface NavigationState {
  // Core state
  expanded: boolean
  selectedItemId: string | undefined
  isMobile: boolean

  // Configuration
  position: Position
  effectivePosition: Position
  canExpand: boolean
  showSettings: boolean
  showAuth: boolean
  isLoggedIn: boolean
  showCoachAI: boolean
}

// Create a reactive state object
const state = reactive<NavigationState>({
  // Initial state
  expanded: false,
  selectedItemId: 'study',
  isMobile: false,

  // Default configuration
  position: defaultConfig.position,
  effectivePosition: defaultConfig.position,
  canExpand: defaultConfig.canExpand,
  showSettings: defaultConfig.showSettings,
  showAuth: defaultConfig.showAuth,
  isLoggedIn: defaultConfig.isLoggedIn,
  showCoachAI: defaultConfig.showCoachAI
})

// Navigation store actions
export const navigationStore = {
  // State accessor
  get state() {
    return state
  },

  // Actions
  setExpanded(expanded: boolean) {
    state.expanded = expanded
  },

  toggleExpanded() {
    state.expanded = !state.expanded
  },

  setSelectedItemId(id: string | undefined) {
    state.selectedItemId = id
  },

  selectItem(id: string, items: NavItem[]) {
    state.selectedItemId = id
    const selectedItem = items.find((item) => item.id === id)
    if (selectedItem?.onClick && !selectedItem.disabled) {
      selectedItem.onClick()
    }
  },

  setIsMobile(isMobile: boolean) {
    state.isMobile = isMobile
    this.calculateEffectivePosition()
  },

  updateConfig(
    config: Partial<{
      position: Position
      canExpand: boolean
      showSettings: boolean
      showAuth: boolean
      isLoggedIn: boolean
      showCoachAI: boolean
    }>
  ) {
    merge(state, config)
    this.calculateEffectivePosition()
  },

  calculateEffectivePosition() {
    const effectivePosition = state.isMobile ? 'bottom' : 'left'

    state.effectivePosition = effectivePosition
  }
}

// Export a function to access the navigationStore
// This maintains compatibility with the component API
export const useNavigationStore = () => navigationStore
